import { typedSupabase } from "../../lib/typed-supabase";
import type { NotificationCategory, NotificationPreferences, NotificationPreferencesUpdate, } from "../../types/notificationPreferences";
import { IService, ServiceResponse, } from "../service-registry";
import { notificationErrorRecoveryService } from "./notification-error-recovery.service";
import { logger } from "../../lib/logger";
export interface INotificationPreferencesService extends IService {
    getUserPreferences(userId?: string): Promise<ServiceResponse<NotificationPreferences>>;
    createDefaultPreferences(userId: string): Promise<ServiceResponse<NotificationPreferences>>;
    updatePreferences(updates: NotificationPreferencesUpdate): Promise<ServiceResponse<NotificationPreferences>>;
    getPreferencesWithDefaults(userId?: string): Promise<ServiceResponse<NotificationPreferences>>;
    shouldSendNotification(userId: string, category: NotificationCategory): Promise<ServiceResponse<boolean>>;
    toggleCategoryPreferences(category: "tasks" | "community" | "admin" | "achievements", enabled: boolean): Promise<ServiceResponse<void>>;
    enableAllNotifications(): Promise<ServiceResponse<void>>;
    disableAllNotifications(): Promise<ServiceResponse<void>>;
}
/**
 * Service for managing user notification preferences
 * Phase 4 Compliant - Implements IService with ServiceResponse wrappers
 */
class NotificationPreferencesService implements INotificationPreferencesService {
    readonly name = "notificationPreferences" as const;
    async initialize(): Promise<void> {
        logger.info('NotificationPreferencesService initialized');
    }
    async dispose(): Promise<void> {
        logger.info('NotificationPreferencesService disposed');
    }
    private async handleServiceError(error: unknown, operation: string): Promise<ServiceResponse<never>> {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error(`Error in ${operation}`, { error: errorMessage });
        return { success: false, error: errorMessage, code: `NOTIFICATION_PREFERENCES_${operation}_ERROR`, service: this.name };
    }
    /**
     * Get user's notification preferences
     */
    async getUserPreferences(userId?: string): Promise<ServiceResponse<NotificationPreferences>> {
        const result = await notificationErrorRecoveryService.withErrorRecovery(async (): Promise<ServiceResponse<NotificationPreferences>> => {
            let targetUserId = userId;
            if (!targetUserId) {
                // Enhanced authentication with session recovery
                try {
                    const { data: { user }, error: authError, } = await typedSupabase.auth.getUser();
                    if (authError || !user) {
                        logger.warn("🔐 Authentication issue in getUserPreferences, attempting session recovery...");
                        // Try to refresh the session
                        const { data: sessionData, error: refreshError } = await typedSupabase.auth.refreshSession();
                        if (refreshError || !sessionData.session?.user) {
                            throw new Error("Authentication failed. Please sign in again to continue.");
                        }
                        logger.info("✅ Session recovered successfully for getUserPreferences");
                        targetUserId = sessionData.session.user.id;
                    }
                    else {
                        targetUserId = user.id;
                    }
                }
                catch (sessionError) {
                    throw new Error("Authentication failed. Please sign in again to continue.");
                }
            }
            const { data, error } = await typedSupabase
                .from("notification_preferences")
                .select("*")
                .eq("user_id", targetUserId)
                .single();
            if (error) {
                if (error.code === "PGRST116") {
                    // No preferences found, create defaults
                    const defaultResult = await this.createDefaultPreferences(targetUserId);
                    if (!defaultResult.success) {
                        return defaultResult;
                    }
                    return defaultResult;
                }
                logger.error("Database error getting preferences", { error: error.message, code: error.code });
                return this.handleServiceError(error, "getUserPreferences");
            }
            return { success: true, data: data as NotificationPreferences };
        }, "getUserPreferences");
        if (!result) {
            return { success: false, error: "Error recovery failed to return a result", code: "NOTIFICATION_PREFERENCES_getUserPreferences_ERROR", service: this.name };
        }
        return result;
    }
    /**
     * Create default notification preferences for a user
     */
    async createDefaultPreferences(userId: string): Promise<ServiceResponse<NotificationPreferences>> {
        const result = await notificationErrorRecoveryService.withErrorRecovery(async (): Promise<ServiceResponse<NotificationPreferences>> => {
            // Ensure we have a valid session before attempting creation
            try {
                const { data: { user }, error: authError, } = await typedSupabase.auth.getUser();
                if (authError || !user || user.id !== userId) {
                    logger.warn("🔐 Authentication issue in createDefaultPreferences, attempting session recovery...");
                    // Try to refresh the session
                    const { data: sessionData, error: refreshError } = await typedSupabase.auth.refreshSession();
                    if (refreshError || !sessionData.session) {
                        throw new Error("Authentication failed. Please sign in again to continue.");
                    }
                    logger.info("✅ Session recovered successfully for notification preferences");
                }
            }
            catch (sessionError) {
                throw new Error("Authentication failed. Please sign in again to continue.");
            }
            const { data, error } = await typedSupabase
                .from("notification_preferences")
                .insert({
                user_id: userId,
                // Default values are set in the database schema
            })
                .select()
                .single();
            if (error) {
                logger.error("Database error creating preferences", { error: error.message, code: error.code });
                return { success: false, error: error.message, code: "PREFERENCES_CREATE_ERROR", service: this.name };
            }
            return { success: true, data: data as NotificationPreferences };
        }, "createDefaultPreferences");
        if (!result) {
            return { success: false, error: "Error recovery failed to return a result", code: "NOTIFICATION_PREFERENCES_createDefaultPreferences_ERROR", service: this.name };
        }
        return result;
    }
    /**
     * Update user's notification preferences
     */
    async updatePreferences(updates: NotificationPreferencesUpdate): Promise<ServiceResponse<NotificationPreferences>> {
        const result = await notificationErrorRecoveryService.withErrorRecovery(async (): Promise<ServiceResponse<NotificationPreferences>> => {
            const { data: { user }, error: authError, } = await typedSupabase.auth.getUser();
            if (authError || !user) {
                logger.error("Authentication error updating preferences", {
                    error: authError?.message || "No user found"
                });
                throw new Error("User not authenticated");
            }
            // Add some logging for debugging
            logger.info("Updating preferences for user", { userId: user.id, updates });
            const { data, error } = await typedSupabase
                .from("notification_preferences")
                .update({
                ...updates,
                updated_at: new Date().toISOString(),
            })
                .eq("user_id", user.id)
                .select()
                .single();
            if (error) {
                logger.error("Database error updating preferences", { error: error.message, code: error.code });
                throw error;
            }
            return { success: true, data: data as NotificationPreferences };
        }, "updatePreferences");
        if (!result) {
            return { success: false, error: "Error recovery failed to return a result", code: "NOTIFICATION_PREFERENCES_updatePreferences_ERROR", service: this.name };
        }
        return result;
    }
    /**
     * Get notification preferences with defaults
     */
    async getPreferencesWithDefaults(userId?: string): Promise<ServiceResponse<NotificationPreferences>> {
        const preferences = await this.getUserPreferences(userId);
        // Return preferences or defaults
        if (preferences.success && preferences.data) {
            return preferences;
        }
        const defaults: NotificationPreferences = {
            id: "",
            user_id: userId || "",
            // Default notification preferences
            task_assignments: true,
            task_status_changes: true,
            task_due_reminders: true,
            task_overdue_alerts: true,
            task_comments: true,
            task_mentions: true,
            task_priority_changes: true,
            task_attachments: true,
            subtask_completions: true,
            post_likes: true,
            post_comments: true,
            comment_replies: true,
            community_mentions: true,
            role_changes: true,
            system_announcements: true,
            admin_actions: true,
            xp_milestones: true,
            streak_bonuses: true,
            achievement_badges: true,
            leaderboard_updates: true,
            email_notifications: true,
            push_notifications: true,
            discord_notifications: false,
            whatsapp_notifications: false,
            telegram_notifications: false,
            slack_notifications: false,
            discord_webhook_url: undefined,
            whatsapp_phone_number: undefined,
            telegram_chat_id: undefined,
            slack_team_id: undefined,
            slack_user_id: undefined,
            digest_frequency: "none" as const,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        return { success: true, data: defaults };
    }
    /**
     * Check if user should receive a specific notification category
     */
    async shouldSendNotification(userId: string, category: NotificationCategory): Promise<ServiceResponse<boolean>> {
        try {
            const { data, error } = await typedSupabase.rpc("should_send_notification", {
                target_user_id: userId,
                notification_category: category,
            });
            if (error) {
                return { success: false, error: error.message, code: "PREFERENCES_CHECK_ERROR", service: this.name };
            }
            return { success: true, data: data as boolean };
        }
        catch (error) {
            logger.error("Error checking notification preference", {
                error: error instanceof Error ? error.message : String(error),
                userId,
                category
            });
            // Default to true if we can't check preferences
            return { success: true, data: true };
        }
    }
    /**
     * Bulk toggle preferences for a category
     */
    async toggleCategoryPreferences(category: "tasks" | "community" | "admin" | "achievements", enabled: boolean): Promise<ServiceResponse<void>> {
        try {
            const { data: { user }, } = await typedSupabase.auth.getUser();
            if (!user)
                throw new Error("User not authenticated");
            let updates: Partial<NotificationPreferencesUpdate> = {};
            switch (category) {
                case "tasks":
                    updates = {
                        task_assignments: enabled,
                        task_status_changes: enabled,
                        task_due_reminders: enabled,
                        task_overdue_alerts: enabled,
                        task_comments: enabled,
                        task_mentions: enabled,
                        task_priority_changes: enabled,
                        task_attachments: enabled,
                        subtask_completions: enabled,
                    };
                    break;
                case "community":
                    updates = {
                        post_likes: enabled,
                        post_comments: enabled,
                        comment_replies: enabled,
                        community_mentions: enabled,
                    };
                    break;
                case "admin":
                    updates = {
                        role_changes: enabled,
                        system_announcements: enabled,
                        admin_actions: enabled,
                    };
                    break;
                case "achievements":
                    updates = {
                        xp_milestones: enabled,
                        streak_bonuses: enabled,
                        achievement_badges: enabled,
                        leaderboard_updates: enabled,
                    };
                    break;
            }
            const result = await this.updatePreferences(updates);
            if (!result.success) {
                return { success: false, error: result.error };
            }
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Error toggling category preferences", {
                error: error instanceof Error ? error.message : String(error),
                category,
                enabled
            });
            return { success: false, error: error instanceof Error ? error.message : "Unknown error", code: "PREFERENCES_TOGGLE_ERROR", service: this.name };
        }
    }
    /**
     * Enable all notifications
     */
    async enableAllNotifications(): Promise<ServiceResponse<void>> {
        const updates: NotificationPreferencesUpdate = {
            // Task Management
            task_assignments: true,
            task_status_changes: true,
            task_due_reminders: true,
            task_overdue_alerts: true,
            task_comments: true,
            task_mentions: true,
            task_priority_changes: true,
            task_attachments: true,
            subtask_completions: true,
            // Community
            post_likes: true,
            post_comments: true,
            comment_replies: true,
            community_mentions: true,
            // Administrative
            role_changes: true,
            system_announcements: true,
            admin_actions: true,
            // Achievements
            xp_milestones: true,
            streak_bonuses: true,
            achievement_badges: true,
            leaderboard_updates: true,
            // General
            email_notifications: true,
            push_notifications: true,
            discord_notifications: true,
            whatsapp_notifications: true,
        };
        const updateResult = await this.updatePreferences(updates);
        if (!updateResult.success) {
            return { success: false, error: updateResult.error };
        }
        return { success: true, data: undefined };
    }
    /**
     * Disable all notifications (keep only essential ones)
     */
    async disableAllNotifications(): Promise<ServiceResponse<void>> {
        const updates: NotificationPreferencesUpdate = {
            // Task Management - keep essential ones
            task_assignments: true, // Keep assignments
            task_status_changes: false,
            task_due_reminders: false,
            task_overdue_alerts: false,
            task_comments: false,
            task_mentions: true, // Keep mentions
            task_priority_changes: false,
            task_attachments: false,
            subtask_completions: false,
            // Community
            post_likes: false,
            post_comments: false,
            comment_replies: false,
            community_mentions: true, // Keep mentions
            // Administrative - keep essential ones
            role_changes: true,
            system_announcements: true,
            admin_actions: false,
            // Achievements
            xp_milestones: false,
            streak_bonuses: false,
            achievement_badges: false,
            leaderboard_updates: false,
            // General
            email_notifications: false,
            push_notifications: true, // Keep push for essential notifications
            discord_notifications: false,
            whatsapp_notifications: false,
        };
        const updateResult = await this.updatePreferences(updates);
        if (!updateResult.success) {
            return { success: false, error: updateResult.error };
        }
        return { success: true, data: undefined };
    }
}
export const notificationPreferencesService = new NotificationPreferencesService();
